import { useState } from 'react';
import { Icon } from '@iconify/react';
import { MessageSquare, Brain, Users, Shield, Play, Database, BarChart3 } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function WhatsAppBusinessLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Connect WhatsApp Business API
Set up your WhatsApp Business account

# 2. Configure Memara webhook
Add Memara webhook to your WhatsApp Business

# 3. Start capturing memories
Customer conversations become searchable memories

# 4. Search customer knowledge
Find any customer interaction instantly`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=PPOb0_DMo7s', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="whatsapp_business"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 shadow" aria-label="WhatsApp Business Logo">
          <Icon icon="logos:whatsapp-icon" width={48} height={48} />
        </span>
      }
      title="WhatsApp Business Integration"
      description="Enterprise customer intelligence for WhatsApp Business communications. Transform customer conversations into comprehensive business intelligence with automated capture, analysis, and searchable customer relationship management for superior customer service and business insights."
      features={[
        { icon: <MessageSquare className="h-4 w-4 text-green-600" />, title: 'Comprehensive Customer Conversation Capture', description: 'Automatically capture and analyze all WhatsApp Business customer interactions with intelligent context extraction and relationship mapping.' },
        { icon: <Brain className="h-4 w-4 text-green-600" />, title: 'Advanced Business Intelligence', description: 'AI-powered insights from customer communications with sentiment analysis, trend identification, and predictive customer behavior analytics.' },
        { icon: <Users className="h-4 w-4 text-green-600" />, title: 'Enterprise Team Collaboration', description: 'Shared customer knowledge base across business teams with role-based access controls and collaborative customer intelligence management.' },
        { icon: <Shield className="h-4 w-4 text-green-600" />, title: 'Enterprise Business Security', description: 'Enterprise-grade security for customer data with multi-tenant isolation, compliance frameworks, and comprehensive audit trails.' },
        { icon: <Database className="h-4 w-4 text-green-600" />, title: 'Customer Relationship Intelligence', description: 'Build comprehensive customer profiles with interaction history, preferences, and intelligent relationship mapping for personalized service.' },
        { icon: <BarChart3 className="h-4 w-4 text-green-600" />, title: 'Business Analytics & Reporting', description: 'Advanced analytics dashboard with customer insights, communication patterns, and business performance metrics for data-driven decisions.' },
      ]}
      setupSteps={[
        'Connect WhatsApp Business API to Memara platform',
        'Configure intelligent webhook integration and data mapping',
        'Start automated customer conversation capture and analysis',
        'Access comprehensive customer intelligence and business insights',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-green-600 hover:bg-green-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-green-200 text-green-600 hover:bg-green-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-green-200 text-green-600 hover:bg-green-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:whatsapp-icon" width={20} height={20} />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=PPOb0_DMo7s"
      microcopy="Trusted by enterprise businesses, customer service teams, and SMEs using WhatsApp Business for customer communication, relationship management, and business intelligence."
      trustedBy="WhatsApp Business, Enterprise Customer Teams, and more."
      testimonial="Memara's WhatsApp Business integration transforms our customer communications into a comprehensive intelligence system that provides unprecedented insights into customer behavior, preferences, and business opportunities while maintaining enterprise-grade security and compliance."
    />
  );
}